import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { SkeletonLoaderService } from 'app/shared/services/skeleton-loader.service';

import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { SkeletonLoaderComponent } from 'app/shared/components/skeleton-loader/skeleton-loader.component';

import { IMediaLoadingState, IVideoEvent } from 'app/shared/interfaces/media.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';

import { EImageUsage } from 'app/shared/enums/image-usage.enums';
import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { ECreatorVideoTypes } from 'app/enums/creator.enums';
import { EVideoEventType } from 'app/shared/enums/video-state.enums';

@Component({
  selector: 'stxt-video',
  standalone: true,
  imports: [CommonModule, SkeletonLoaderComponent, SvgIconComponent],
  templateUrl: './video.component.html',
  styleUrl: './video.component.scss'
})
export class VideoComponent implements OnInit, OnDestroy {
  @Input() src: string = '';
  @Input() videoId: string = '';
  @Input() alt: string = 'Video';
  @Input() mediaUsage: EImageUsage = EImageUsage.Gallery;
  @Input() tabindex: number = -1;
  @Input() controls: boolean = true;
  @Input() muted: boolean = false;
  @Input() loop: boolean = false;
  @Input() preload: 'none' | 'metadata' | 'auto' = 'metadata';
  @Input() showPlayButton: boolean = false;
  @Input() videoType: ECreatorVideoTypes = ECreatorVideoTypes.Preview;

  @Output() videoEvent: EventEmitter<IVideoEvent> = new EventEmitter<IVideoEvent>();

  public loadingState: IMediaLoadingState = {
    isLoaded: false,
    isRendered: false,
    isError: false
  };

  public isPlaying: boolean = false;

  public playIcon: ISvgConfig = {
    name: 'play_black',
    fill: ESvgTypes.None
  };

  public pauseIcon: ISvgConfig = {
    name: 'pause',
    fill: ESvgTypes.None
  };
  protected readonly ECreatorVideoTypes = ECreatorVideoTypes;

  private readonly skeletonTimeout?: ReturnType<typeof globalThis.setTimeout>;
  private showSkeleton: boolean = false;

  constructor(private readonly skeletonLoaderService: SkeletonLoaderService) {}

  ngOnInit(): void {
    this.initializeLoadingState();
  }

  private initializeLoadingState(): void {
    this.loadingState = {
      isLoaded: false,
      isRendered: false,
      isError: false
    };

    this.showSkeleton = true;
    this.emitStateChange();
  }

  onVideoStateChange(event: Event): void {
    const eventType = event.type;

    // Handle different event types
    switch (eventType) {
      case 'loadedmetadata':
        this.loadingState.isLoaded = true;
        this.handleSafariThumbnail();
        break;

      case 'loadeddata':
        this.loadingState.isLoaded = true;
        this.videoEvent.emit({ type: EVideoEventType.LoadedData, event });
        break;

      case 'canplay':
      case 'canplaythrough':
        this.loadingState = {
          ...this.loadingState,
          isLoaded: true,
          isRendered: true
        };
        this.showSkeleton = false;
        this.videoEvent.emit({ type: EVideoEventType.CanPlay, event });
        break;

      case 'error':
        this.loadingState = {
          isError: true,
          isLoaded: true,
          isRendered: true
        };
        this.showSkeleton = false;
        this.videoEvent.emit({ type: EVideoEventType.Error, event });
        break;

      case 'ended':
        this.isPlaying = false;
        break;
    }

    this.skeletonLoaderService.handleVideoStateChange(event, this.loadingState);
    this.emitStateChange();
  }

  private handleSafariThumbnail(): void {
    const video = document.getElementById(this.videoId) as HTMLVideoElement;
    if (!video) return;

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari && video.readyState >= 1) {
      video.currentTime = 0.1;

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        if (!this.isPlaying && !video.paused) {
          video.pause();
        }
      };
      video.addEventListener('seeked', onSeeked);
    }
  }

  onTouchStart(event: TouchEvent): void {
    event.preventDefault();
  }

  onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleVideoPlayback();
  }

  onVideoClick(event: Event): void {
    if (event.type === 'click' && event instanceof MouseEvent) {
      event.stopPropagation();
      this.videoEvent.emit({ type: EVideoEventType.Click, event });
    }
  }

  private toggleVideoPlayback(event?: Event): void {
    const video = document.getElementById(this.videoId) as HTMLVideoElement;
    if (!video) return;

    if (video.paused) {
      video.play();
      this.isPlaying = true;
      this.videoEvent.emit({
        type: EVideoEventType.Play,
        event,
        videoId: this.videoId
      });
    } else {
      video.pause();
      this.isPlaying = false;
      this.videoEvent.emit({
        type: EVideoEventType.Pause,
        event,
        videoId: this.videoId
      });
    }
  }

  togglePlayPause(event: Event): void {
    event.stopPropagation();
    this.toggleVideoPlayback(event);
  }

  shouldShowSkeleton(): boolean {
    return !this.loadingState.isRendered && !this.loadingState.isError && this.showSkeleton;
  }

  getUsageClass(): string {
    const usageClassMap: Partial<{ [key in EImageUsage]: string }> = {
      [EImageUsage.Gallery]: 'gallery',
      [EImageUsage.Profile]: 'profile',
      [EImageUsage.MessageMedia]: 'discover'
    };
    return usageClassMap[this.mediaUsage] || '';
  }

  private emitStateChange(): void {
    this.videoEvent.emit({
      type: EVideoEventType.StateChange,
      state: { ...this.loadingState }
    });
  }

  public playVideo(): void {
    const video = document.getElementById(this.videoId) as HTMLVideoElement;
    if (video?.paused) {
      video.play();
      this.isPlaying = true;
    }
  }

  public pauseVideo(): void {
    const video = document.getElementById(this.videoId) as HTMLVideoElement;
    if (video && !video.paused) {
      video.pause();
      this.isPlaying = false;
    }
  }

  public resetVideo(): void {
    const video = document.getElementById(this.videoId) as HTMLVideoElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
      this.isPlaying = false;
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.skeletonTimeout);
  }
}
